/** SEO URL website-cache LMDB stores (Start Cache only on DBs inside these). */
export const WEBSITE_SEO_CACHE_STORE_IDS = [
  'backend-desktop-cache',
  'backend-mobile-website-cache',
  'backend-mobile-app-cache',
] as const;

export type WebsiteSeoCacheStoreId =
  (typeof WEBSITE_SEO_CACHE_STORE_IDS)[number];

export type WebsiteCacheStartConfigBody = {
  cacheMode: 'desktop' | 'mobile_website' | 'mobile_app';
  platform?: 'ios' | 'android';
  version?: string;
};

const DESKTOP_CACHE_DBS = new Set(['desktop-web:cache']);

const MOBILE_WEB_CACHE_DBS = new Set(['mobile-web:cache']);

const MOBILE_APP_BASE_DB = 'mobile-app:cache';
const MOBILE_APP_VERSIONED_PREFIX = 'mobile-app:cache:';

export function isWebsiteSeoCacheStore(storeId: string | null): boolean {
  return (
    !!storeId &&
    (WEBSITE_SEO_CACHE_STORE_IDS as readonly string[]).includes(storeId)
  );
}

/**
 * Build start-config body from selected store + database name.
 *
 * Version lists live in env (MOBILE_APP_WARM_*) and show up as LMDB DB names —
 * do not hardcode versions/platforms here. For versioned DBs we only parse the
 * version suffix from `mobile-app:cache:{version}`; platform is chosen in UI.
 */
export function buildWebsiteCacheConfigFromDb(
  storeId: string,
  dbName: string,
): WebsiteCacheStartConfigBody | null {
  if (!isWebsiteSeoCacheStore(storeId) || !dbName) {
    return null;
  }

  if (storeId === 'backend-desktop-cache' && DESKTOP_CACHE_DBS.has(dbName)) {
    return {cacheMode: 'desktop'};
  }

  if (
    storeId === 'backend-mobile-website-cache' &&
    MOBILE_WEB_CACHE_DBS.has(dbName)
  ) {
    return {cacheMode: 'mobile_website'};
  }

  if (storeId === 'backend-mobile-app-cache') {
    if (dbName === MOBILE_APP_BASE_DB) {
      // Version + platform collected in UI (env versions differ per stage/prod).
      return {cacheMode: 'mobile_app'};
    }

    if (dbName.startsWith(MOBILE_APP_VERSIONED_PREFIX)) {
      const version = dbName.slice(MOBILE_APP_VERSIONED_PREFIX.length).trim();
      if (!version) {
        return {cacheMode: 'mobile_app'};
      }
      // Version from LMDB DB name only — never infer ios/android from version shape.
      return {cacheMode: 'mobile_app', version};
    }
  }

  return null;
}

export function formatWebsiteCacheConfigLabel(
  body: WebsiteCacheStartConfigBody,
): string {
  if (body.cacheMode === 'mobile_app') {
    return `mobile_app / ${body.platform ?? '?'} / ${body.version ?? '?'}`;
  }
  return body.cacheMode;
}
